<template>
    <div class="subscription absolute inset-0 flex items-center justify-center">
        <div class="subscription-box bg-white rounded-lg flex flex-col items-center justify-center">
            <img class="subscription-logo cursor-pointer mt-4" src="@/assets/images/auth-logo.svg"
            @click="$router.push({ name: 'Auth' })">
            
            <img class="subscription-illus hidden lg:block rounded-full" src="@/assets/images/auth-illus.jpg">

            <div 
                class="subscription-form flex flex-col items-center
                justify-evenly border border-blue rounded-md my-4"
            >
                <!-- Title -->
                <h1 class="uppercase text-xs font-main-bold">
                    Inscription
                </h1>
                
                <!-- Step 1 -->
                <div
                v-if="step == 0"
                @keyup.enter="nextStep"
                class="subscription-step">
                    <p>Nom, prénom *</p>
                    <div class="subscription-inputs border border-blue border-opacity-50 rounded">
                        <input type="text" name="lastname" placeholder="Nom" v-model="lastname" ref="name">
                        <input type="text" name="firstname" placeholder="Prénom" v-model="firstname">
                    </div>
                    <p
                    class="subscription-step-error"
                    :class="{ 'opacity-0': !stepError }">
                        {{stepError}}
                    </p>
                </div>

                <!-- Step 2 -->
                <div
                v-if="step == 1"
                @keyup.enter="nextStep"
                class="subscription-step">
                    <p>Email, mot de passe *</p>
                    <div class="subscription-inputs border border-blue border-opacity-50 rounded">
                        <input type="text" name="email" placeholder="Email"
                        :disabled="hasPendingRequest"
                        ref="email"
                        v-model="email">
                        <input type="password" name="password" placeholder="Mot de passe"
                        :disabled="hasPendingRequest"
                        v-model="password">
                    </div>
                    <p
                    class="subscription-step-error"
                    :class="{ 'opacity-0': !stepError }">
                        {{stepError}}
                    </p>
                </div>

                <!-- Step 3 -->
                <div
                v-if="step == 2"
                @keyup.enter="nextStep"
                class="subscription-step">
                    <p>Statut *</p>
                    <v-select
                    class="faa-select mb-5"
                    name="status"
                    :options="statusList"
                    :clearable="false"
                    :searchable="false"
                    v-model="status"
                    ></v-select>

                    <template v-if="status.id < 2">
                        <p>OPH *</p>
                        <v-select
                        class="faa-select mb-4"
                        name="organization"
                        :options="organizationList"
                        :clearable="false"
                        :searchable="true"
                        v-model="organization"
                        ></v-select>
                    </template>

                    <div 
                    v-if="status.id > 1"
                    class="subscription-inputs border border-blue border-opacity-20 rounded">
                        <input class="placeholder-gray" type="text" name="position" placeholder="Fonction" v-model="position">
                        <input class="placeholder-gray" type="text" name="company" placeholder="Employeur" v-model="company">
                    </div>
                    <p
                    class="subscription-step-error"
                    :class="{ 'opacity-0': !stepError }">
                        {{stepError}}
                    </p>
                </div>

                <!-- Step 4 -->
                <div
                v-if="step == 3"
                @keyup.enter="nextStep"
                class="subscription-step">
                    <p>Type de navigation *</p>
                    <v-select
                    class="faa-select mb-1"
                    name="navigation"
                    :options="navigationList"
                    :clearable="false"
                    :searchable="false"
                    v-model="navigation"
                    ></v-select>
                    <span class="mb-4" v-if="status.id !== 1">
                        Si vous avez été nouvellement nommé, préférez la <u>navigation guidée</u>, si vous êtes experimenté, préférez la <u>navigation libre</u>.
                    </span>

                    <template v-if="status.id < 1">
                        <p>Ancienneté *</p>
                        <v-select
                        class="faa-select mb-4"
                        name="seniority"
                        :options="seniorityList"
                        :clearable="false"
                        :searchable="false"
                        v-model="seniority"
                        ></v-select>
                        <p
                        v-if="stepError"
                        class="subscription-step-error"
                        :class="{ 'opacity-0': !stepError }">
                            {{stepError}}
                        </p>
                    </template>
                </div>

                <!-- Step 5 -->
                <div
                v-if="step == 4"
                class="subscription-step">
                    <p>J’ai déjà participé à … *</p>
                    <div class="subscription-inputs border border-blue border-opacity-20 rounded py-2 px-4">
                        <label class="subscription-checkbox">
                            <span>
                                Un séminaire organisé par la Fédération des OPH
                            </span>
                            <input
                            class="faa-checkbox"
                            type="checkbox"
                            name="experience_seminar"
                            v-model="experience_seminar">
                        </label>

                        <label class="subscription-checkbox">
                            <span>
                                Une formation dédiée aux administrateurs
                            </span>
                            <input
                            class="faa-checkbox"
                            type="checkbox"
                            name="experience_course"
                            v-model="experience_course">
                        </label>

                        <label class="subscription-checkbox">
                            <span>
                                D'autres formations sur le logement social
                            </span>
                            <input
                            class="faa-checkbox"
                            type="checkbox"
                            name="experience_other"
                            v-model="experience_other">
                        </label>
                    </div>
                    <p
                    class="subscription-step-error"
                    :class="{ 'opacity-0': !stepError }">
                        {{stepError}}
                    </p>
                </div>

                <!-- Step 6 -->
                <div
                v-if="step == 5"
                class="subscription-step">
                    <label class="subscription-checkbox">
                        <input
                        class="faa-checkbox"
                        type="checkbox"
                        name="termsOfUse"
                        :disabled="hasPendingRequest"
                        v-model="termsOfUse">
                        <span>
                            J’accepte les <a class="m-0 underline" @click.prevent="showLegals = true">conditions d’utilisation du site</a> *
                        </span>
                    </label>

                    <label class="subscription-checkbox">
                        <input
                        class="faa-checkbox"
                        type="checkbox"
                        name="agreeMailing"
                        :disabled="hasPendingRequest"
                        v-model="agreeMailing">
                        <span>
                            J'accepte de recevoir de l'information de la Fédération des OPH
                        </span>
                    </label>
                    <p
                    class="subscription-step-error flex justify-center"
                    :class="{ 'opacity-0': !stepError }">
                        {{stepError}}
                    </p>
                </div>

                <!-- Buttons -->
                <button
                v-if="step < 5"
                class="subscription-submit faa-button-blue"
                :class="{
                    'loading': hasPendingRequest
                }"
                :disabled="hasPendingRequest || !filledRequeriedFields"
                @click.prevent="nextStep">
                    Suivant
                    <div class="faa-loader">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </button>

                <button
                v-else
                type="submit"
                class="subscription-submit faa-button-blue"
                :class="{
                    'loading': hasPendingRequest
                }"
                :disabled="hasPendingRequest || !filledRequeriedFields"
                @click.prevent="submit">
                    Valider mon profil
                    <div class="faa-loader">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </button>
            </div>
        </div>

        <!-- Legals Modal -->
        <div class="modal-container absolute inset-0 flex flex-col items-center justify-center" v-if="showLegals">
            <div
            class="absolute subscription-modal bg-white rounded-md shadow-dark z-10 flex flex-col mb-28 mt-4">

                <!-- Modal Header -->
                <div class="subscription-modal-header relative z-1">
                    <!-- PDF Header -->
                    <PDFViewHeader
                    :title="legalsPDF.title"
                    :pdf="legalsPDF"
                    @previous="previousPDFPage()"
                    @next="nextPDFPage()"
                    @close="showLegals = false"/>
                </div>

                <!-- Modal content -->
                <div class="subscription-modal-content">
                    <!-- PDF -->
                    <PDFView
                    :pdf="legalsPDF" 
                    ref="pdf" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import PDFViewHeader from '@/components/scenario/modal/PDFViewHeader'
    import PDFView from '@/components/scenario/modal/PDFView'

    export default {
        name: 'Subscription',
        components: {
            PDFViewHeader,
            PDFView,
        },
        data(){
            return {
                // UI state
                step: 0,
                pendingRequest: 0,
                stepError: false,
                showLegals: false,
                legalsPDF: {
                    title: 'CGU',
                    url: '/CGU.pdf',
                    type: {
                        slug: 'file_pdf',
                    },
                },
                // Data state
                firstname: '',
                lastname: '',
                email: '',
                password: '',
                status: {
                    id: 0,
                    label: 'Administrateur dans un OPH'
                },
                organization: {
                    id: 0,
                    label: 'Grand Lyon Habitat',
                },
                position: '',
                company: '',
                navigation: {
                    id: 0,
                    label: 'Guidée',
                },
                seniority: {
                    id: 0,
                    label: 'Administrateur nouvellement nommé',
                },
                experience_seminar: false,
                experience_course: false,
                experience_other: false,
                termsOfUse: false,
                agreeMailing: false,
                // Data source
                statusList: [
                    {
                        id: 0,
                        label: 'Administrateur dans un OPH'
                    },
                    {
                        id: 1,
                        label: 'Collaborateur dans un OPH'
                    },
                    {
                        id: 2,
                        label: 'Autre',
                    }
                ],
                navigationList: [
                    {
                        id: 0,
                        label: 'Guidée',
                    },
                    {
                        id: 1,
                        label: 'Libre',
                    },
                ],
                seniorityList: [
                    {
                        id: 0,
                        label: 'Administrateur nouvellement nommé',
                    },
                    {
                        id: 1,
                        label: 'Administrateur expérimenté',
                    },
                ],
            }
        },
        computed: {
            hasPendingRequest() {
                return (this.pendingRequest > 0)
            },
            filledRequeriedFields() {
                switch (this.step) {
                    case 0:
                        return (this.firstname.trim().length >= 2 && this.lastname.trim().length >= 2)
                    
                    case 1: {
                        const re = /\S+@\S+\.\S+/
                        return (this.email.trim().length > 0 && this.password.trim().length > 0 && re.test(this.email))
                    }
                    
                    case 2:
                        if (this.status.id < 2) {
                            return (this.organization ? true : false)
                        } else {
                            return (this.position.trim().length > 0 && this.company.trim().length > 0)
                        }
                    
                    case 3:
                        return true
                    
                    case 4:
                        return true

                    case 5:
                        return this.termsOfUse
                    
                    
                }
                return true
            },
            organizationList() {
                // Add label and sort
                return this.$store.state.Organisations.list.filter((orga) => {
                    return (orga.post_code && orga.post_code.trim() != '')
                }).map((orga) => {
                    orga.label = orga.post_code.substring(0, 2) + ' - ' + orga.name 

                    return orga
                }).sort((orga1, orga2) => {
                    return orga1.label.localeCompare(orga2.label)
                })
            },
        },
        async created() {
            await this.$store.dispatch('Organisations/getList')
        },
        mounted() {
            // Autofocus name field
            this.$nextTick(() => {
                if (this.$refs.name) {
                    this.$refs.name.focus()
                }
            })
        },
        watch: {
            organizationList: {
                handler(list) {
                    if (list && list.length > 0) {
                        this.organization = list[0]
                    } else {
                        this.organization = null
                    }
                },
                immediate: true,
            },
        },
        methods: {
            async dispatchRequest(action_name, args = null) {
                this.pendingRequest += 1

                let response = null

                if (args) {
                    response = await this.$store.dispatch(action_name, args)
                } else {
                    response = await this.$store.dispatch(action_name)
                }

                this.pendingRequest -= 1
                
                return response
            },
            async nextStep() {
                if (this.hasPendingRequest || !this.filledRequeriedFields || this.step >= 5)
                    return

                switch (this.step) {
                    case 1: {
                        this.stepError = false

                        const valid = await this.isEmailValid()
                        if (!valid) {
                            this.stepError = 'Cette addresse email est déjà utilisée'
                            if (this.$store.state.Auth.errorCode) {
                                this.stepError += ' (' + this.$store.state.Auth.errorCode + ' - ' + this.$store.state.Auth.errorMessage + ')'
                            }
                            return
                        }
                        break
                    }
                }

                this.step += 1

                switch (this.step) {
                    case 1:
                        // Autofocus email field
                        this.$nextTick(() => {
                            if (this.$refs.email) {
                                this.$refs.email.focus()
                            }
                        })
                        break
                }
            },
            async isEmailValid() {
                // Check email value
                const re = /\S+@\S+\.\S+/
                const email = (this.email ? this.email.trim() : null)
                
                if (!email || email.length <= 0 || !re.test(email))
                    return false

                // Check if email exist
                const exist = await this.dispatchRequest('Auth/emailExist', email)

                return !exist
            },
            async submit() {
                if (this.hasPendingRequest || !this.filledRequeriedFields)
                    return

                this.stepError = false

                const res = await this.dispatchRequest('Auth/subscription', {
                    email: this.email,
                    password: this.password,
                    firstname: this.firstname,
                    lastname: this.lastname,
                })
                
                // todo: error handling
                if (!res || !res.data || !res.data.user.id) {
                    this.stepError = 'Erreur lors de l\'inscription'
                    return
                }

                // Add user data to the newly created user
                await this.dispatchRequest('Auth/updateUser', {
                    id: res.data.user.id,
                    experienced: (this.navigation.id == 1),
                    fName: this.firstname,
                    lName: this.lastname,
                    status: this.status.id,
                    organization: this.organization,
                    position: this.position,
                    company: this.company,
                    seniority: this.seniority.id,
                    experienceSeminar: this.experience_seminar,
                    experienceCourse: this.experience_course,
                    experienceOther: this.experience_other,
                    mailing: this.agreeMailing,
                })

                // Check if we need & can show the first login scenario
                await this.dispatchRequest('Locations/getBySlug', 'map_first_login')

                const mapLocation = this.$store.state.Locations.itemsBySlug['map_first_login']

                if (mapLocation && mapLocation.scenarios && mapLocation.scenarios.length > 0) {
                    const id = mapLocation.scenarios[0].scenario_id

                    const logs = await this.$store.dispatch('Logs/getByKeyAndType', {
                        data_key: id,
                        type_slug: 'scenario_start',
                    })

                    if (!logs || logs.length <= 0) {
                        this.$router.push({
                            name: 'Scenario',
                            params: {
                                id,
                                hideHomeButton: true,
                            }
                        })
                        return
                    }
                }
                
                this.$router.push({ name: 'Home' })
            },
            previousPDFPage() {
                if (this.$refs.pdf)
                    this.$refs.pdf.previousPage()
            },
            nextPDFPage() {
                if (this.$refs.pdf)
                    this.$refs.pdf.nextPage()
            },
        },
    }
</script>

<style lang="scss" scoped>

.subscription-box {
    width: 40%;
    height: 90%;
    max-width: 672px;
    max-height: 960px;

    .subscription-logo {
        height: 7.2%;
        margin-bottom: 8%;
    }

    .subscription-illus {
        height: 25%;
        margin-bottom: 5%;
    }

    .subscription-form {
        padding: 0 7.4%;
        width: 80%;
        height: 50%;

        button, input[type='text'], input[type='password'], input[type='email'] {
            @apply block appearance-none;

            &:focus {
                @apply outline-none;
            }
        }

        .subscription-step {
            @apply text-left;
            width: 100%;

            &-error {
                @apply text-red mt-1;
                transition: opacity 0.3s;
            }

            .subscription-inputs {
                width: 100%;

                & > input {
                    @apply w-full bg-transparent m-auto px-4 py-3 text-sm;

                    &::placeholder {
                        @apply text-blue;
                    }
                    
                    &:first-child {
                        @apply border-b border-blue border-opacity-50;
                    }
                } 
            }

            & > p {
                @apply text-left text-xs mb-1;
            }

            & > span {
                @apply block text-left text-xs text-gray-dark;
            }

            .subscription-checkbox {
                @screen md {
                    @apply my-4;
                }

                @apply flex items-center justify-start text-sm cursor-pointer my-1;
                vertical-align: top;
                width: 100%;
                text-align: left;

                span {
                    @apply w-full ml-2;
                }

                input {
                    @apply ml-16 mr-3 flex-shrink-0;
                }
            }
        }

        .subscription-submit {
            @apply p-2;
            @screen lg {
                @apply p-4;
            }
            button:focus {
                @apply shadow-outline;
            }
        }
    }
}

.subscription-modal { // todo: improve responsive
    width: 980px;
    max-width: 90%;
    max-height: 90%;

    &-header {
        box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);// todo
    }

    &-content {
        overflow: auto;
        height: 100%;
    }
}

</style>
